import { Injectable, NgZone } from '@angular/core';
import { Subject            } from 'rxjs/Subject';
import { Storage } from '@ionic/storage';
import { isEmpty } from 'lodash';

import { AUTH_CONFIG_NATIVE, AUTH_CONFIG_WEB } from './auth0.config';

// Auth0 libraries
//
// Web
//
import Auth0Lock from 'auth0-lock';

// Native
//
import Auth0Cordova from '@auth0/cordova';
import * as auth0 from 'auth0-js';



@Injectable()
export class Auth0Provider {

  public loggedInState: Subject<boolean> = new Subject<boolean>();

  private init: any;
  private loading = false;

  private idToken: string;
  private accessToken: string;

  // Web
  //
  private lock: any;

  // Native
  //
  private auth0: any;
  private client: any;

  private nativeOptions = {
    scope: 'openid user_id profile email offline_access'
  };

  private webOptions = {
    auth: {
      params: {
        scope: 'openid user_id profile email'
      },
      responseType: 'id_token token'
    },
    closable: false,
    autofocus: true,
    language: 'nl',
    rememberLastLogin: true,
    languageDictionary: {
        title: "Inloggen op VVU Timetracker"
    },
    theme: {
      logo: 'https://rvz.m-catch.com/admin/assets/images/logo.svg',
      primaryColor: '#002d6a',
    }
  };

  constructor(
    public zone: NgZone,
    private storage: Storage
  ) {
    this.init = {};
    this.init.promise = new Promise( ( resolve, reject ) => {
      this.init.resolve = resolve;
      this.init.reject  = reject;
    } );

    if ( this.useNative() ) {
      this.setupNative();
    } else {
      this.setupWeb();
    }

    // Already an profile or are we not handling an callback with an access token from Auth0? Resolve now.
    // When this is not the case, assume we either show the lock and have the user login himself,
    // or the access token is being processed resulting in either a authenticated event or an authorization error event
    //
    if ( !document.location.hash || !document.location.hash.match( /access_token=/ )) {
      this.init.resolve();
    } else {
      this.storage.get( 'profile' )
      .then( ( profile ) => {
        if ( !isEmpty( profile ) ) {
          this.init.resolve();
        }
      })
    }
  }

  setupNative() {
    console.log( '[SERVICE:AUTH0] Setting up for native login' );

    this.auth0 = new auth0.WebAuth( AUTH_CONFIG_NATIVE );
    this.client = new Auth0Cordova( AUTH_CONFIG_NATIVE );
  }

  setupWeb() {
    console.log( '[SERVICE:AUTH0] Setting up web / lock widget' );

    // Set the Auth0 credentials
    //
    this.lock = new Auth0Lock(
        AUTH_CONFIG_WEB.clientID,
        AUTH_CONFIG_WEB.domain,
        this.webOptions
    );

    this.lock.on ( 'show', () => {
      console.log( '[SERVICE:AUTH0] Showing lock' );
    } );

    this.lock.on ( 'hide', () => {
      console.log( '[SERVICE:AUTH0] Hiding lock' );
    } );

    this.lock.on( 'authorization_error', ( error ) => {
      console.error( '[AUTH0] Auth error', error );

      // Resolve initialization when there was an error
      //
      this.init.resolve();
    } );

    this.lock.on( 'unrecoverable_error', ( error ) => {
      console.error( '[AUTH0] Unrecoverable error', error );
    } );

    // Listening for the authenticated event
    //
    this.lock.on( 'authenticated', ( authResult ) => {

      // Use the token in authResult to getUserInfo() and save it to this.storage
      //
      console.log( '[AUTH0]: result', authResult );

      this.lock.getUserInfo( authResult.accessToken, ( error, profile ) => {
        console.log( '[AUTH0]: user information', profile, error );

        if ( error ) {
          this.loggedInState.next( false );
          console.error( '[SERVICE:AUTH0] Problem getting user info', error );

          // Handle error
          // **TODO**
          //
          return;
        }

        this.idToken      = authResult.idToken;
        this.accessToken  = authResult.accessToken;

        Promise.all( [
          this.storage.set( 'accessToken', authResult.accessToken ),
          this.storage.set( 'idToken',     authResult.idToken     ),
          this.storage.set( 'profile',  JSON.stringify( profile ) )
        ] )
        .then( () => {
          this.zone.run(() => {
            this.loggedInState.next( true );

            // Hide Lock widget
            //
            this.lock.hide();

            // Resolve initialization so the route guard can continue
            //
            this.init.resolve();
          } );
        } );
      } );
    } );
  }

  /**
   * Perform login for native (cordova)
   *
   * @returns
   * @memberof Auth0Provider
   */
  nativeLogin() {

    console.log( '[SERVICE:AUTH0] nativeLogin' );

    if ( this.loading ) { return; }

    this.loading = true;

    // Authorize login request with Auth0: open login page and get auth results
    //
    this.client.authorize( this.nativeOptions, ( error, authResult ) => {
      if( error ) {
        console.error( '[SERVICE:AUTH0] Problem authenticating', error );

        this.loggedInState.next( false );

        this.loading = false;
        return;
      }

      // Store tokens
      //
      this.idToken      = authResult.idToken;
      this.accessToken  = authResult.accessToken;

      this.storage.set( 'accessToken', authResult.accessToken );
      this.storage.set( 'idToken',     authResult.idToken     );

      // Fetch user's profile info
      //
      this.auth0.client.userInfo( authResult.accessToken, ( error, profile ) => {
        if( error ) {
          console.error( '[SERVICE:AUTH0] Problem getting user info', error );

          this.loggedInState.next( false );

          this.loading = false;
          return;
        }

        this.storage.set( 'profile', profile);

        this.loggedInState.next( true );
        this.loading = false;

        // Resolve initialization so the route guard can continue
        //
        this.zone.run(() => this.init.resolve());
      });
    });
  }

  // Show lock popup
  //
  show() {
    this.lock.show();
  }

  // Verify that there's a token in this.storage
  //
  token( type?: string ) {
    switch ( type ) {
      case 'id': return this.idToken;
      default:   return this.accessToken;
    }
  }

  // Return profile, but wait until we have initialized
  //
  profile() {

    return new Promise( ( resolve, reject ) => {
      this.init.promise
      .then( () => {
        return this.storage.get( 'profile' );
      } )
      .then( ( rawProfile ) => {
        console.log( '[SERVICE:AUTH0] rawProfile', rawProfile );

        if ( rawProfile ) {
          resolve( JSON.parse( rawProfile ) );
        } else {
          resolve();
        }
      })
      .catch( reject );
    });
  }

  // Logout the current user
  //
  logout() {
    this.storage.remove( 'accessToken' );
    this.storage.remove( 'idToken'     );
    this.storage.remove( 'profile'     );

    this.idToken      = null;
    this.accessToken  = null;

    if ( this.useNative() ) {
      this.loggedInState.next( false );
      this.auth0.logout( {
        returnTo: `${AUTH_CONFIG_NATIVE.packageIdentifier}://logout`
      });
    } else {
      this.lock.logout();
    }
  }

  /**
   * Are we running on native?
   */
  public useNative() {
    return !!window.hasOwnProperty( 'cordova' );
  }
}



// WEBPACK FOOTER //
// ./src/providers/auth0.provider.ts